/**
 * Chat module Vuex store
 */
import chatService from '../services/chatService';

export default {
  namespaced: true,

  state: {
    isOpen: false,
    messages: [],
    sessionId: null,
    isLoading: false,
    error: null,
  },

  getters: {
    isOpen: (state) => state.isOpen,
    messages: (state) => state.messages,
    isLoading: (state) => state.isLoading,
    sessionId: (state) => state.sessionId,
    error: (state) => state.error,
  },

  mutations: {
    TOGGLE_CHAT(state) {
      state.isOpen = !state.isOpen;
    },

    SET_OPEN(state, isOpen) {
      state.isOpen = isOpen;
    },

    ADD_MESSAGE(state, message) {
      state.messages.push({
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...message,
      });
    },

    APPEND_TO_LAST(state, text) {
      const lastIndex = state.messages.length - 1;
      if (lastIndex >= 0 && state.messages[lastIndex].role === 'assistant') {
        state.messages[lastIndex].content += text;
      }
    },

    UPDATE_LAST_MESSAGE(state, updates) {
      const lastIndex = state.messages.length - 1;
      if (lastIndex >= 0) {
        state.messages[lastIndex] = {
          ...state.messages[lastIndex],
          ...updates,
        };
      }
    },

    SET_LOADING(state, loading) {
      state.isLoading = loading;
    },

    SET_SESSION_ID(state, sessionId) {
      state.sessionId = sessionId;
    },

    CLEAR_MESSAGES(state) {
      state.messages = [];
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_ERROR(state) {
      state.error = null;
    },
  },

  actions: {
    toggleChat({ commit }) {
      commit('TOGGLE_CHAT');
    },

    openChat({ commit }) {
      commit('SET_OPEN', true);
    },

    closeChat({ commit }) {
      commit('SET_OPEN', false);
    },

    async sendMessage({ commit, state, rootState }, text) {
      if (!text.trim()) return;

      commit('CLEAR_ERROR');

      // Add user message
      commit('ADD_MESSAGE', { role: 'user', content: text });

      // Set loading and create placeholder for assistant response
      commit('SET_LOADING', true);
      commit('ADD_MESSAGE', { role: 'assistant', content: '', toolCalls: [] });

      try {
        const token = rootState.auth.token;

        await chatService.sendMessage(
          text,
          state.sessionId,
          token,
          {
            onText: (chunk) => {
              commit('APPEND_TO_LAST', chunk);
            },
            onToolUse: (tool) => {
              // Update last message with tool call info
              const lastMsg = state.messages[state.messages.length - 1];
              commit('UPDATE_LAST_MESSAGE', {
                toolCalls: [...(lastMsg.toolCalls || []), { name: tool.name, status: 'running' }],
              });
            },
            onToolResult: (result) => {
              // Update tool call status
              const lastMsg = state.messages[state.messages.length - 1];
              const toolCalls = [...(lastMsg.toolCalls || [])];
              const lastToolIndex = toolCalls.length - 1;
              if (lastToolIndex >= 0) {
                toolCalls[lastToolIndex] = { ...toolCalls[lastToolIndex], status: 'done' };
              }
              commit('UPDATE_LAST_MESSAGE', { toolCalls });
            },
            onError: (error) => {
              commit('SET_ERROR', error.message || 'An error occurred');
              commit('UPDATE_LAST_MESSAGE', {
                content: 'Sorry, an error occurred. Please try again.',
                isError: true,
              });
            },
            onDone: () => {
              commit('SET_LOADING', false);
            },
          }
        );
      } catch (error) {
        console.error('Send message error:', error);
        commit('SET_ERROR', error.message || 'Failed to send message');
        commit('SET_LOADING', false);
      }
    },

    clearChat({ commit }) {
      commit('CLEAR_MESSAGES');
      commit('SET_SESSION_ID', null);
    },

    setSessionId({ commit }, sessionId) {
      commit('SET_SESSION_ID', sessionId);
    },
  },
};
